import React, { FC, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Formik, Form, FormikHelpers } from 'formik';
import { object, string } from 'yup';

// actions
import { loadResource, createChatRoom, LiveLikeChatContentFilter } from 'ducks/merchantDucks/items/actions';

// types
import { Optional } from 'utils/types';
import { ChatRoomDetails } from 'ducks/merchantDucks/items/types';

// hooks
import useWillMount from 'hooks/useWillMount';

// selectors
import { getItemDetails } from 'ducks/merchantDucks/items/selectors';

// components
import Loader from 'components/Loader';
import FormAccordionHelper, { SectionType } from 'pages/MerchantPages/Assets/components/AccordionHelper';
import AccordionFooter from 'components/AccordionFooter';
import ChatroomField, { Props as ChatroomFieldProps } from './ChatroomField';

// own hooks
import useChatrooms from './hooks/useChatrooms';

const StretchedForm = styled(Form)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const StretchedFlex = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export interface FormValues {
  chatroom: ChatRoomDetails;
  patch?: boolean;
}

export const defaultValues: FormValues = {
  chatroom: {
    id: '',
    title: '',
    content_filter: LiveLikeChatContentFilter.NONE,
  },
  patch: false,
};

export const TOP_FIELD_NAME = 'chatroom';

const validationSchema = object().shape({
  chatroom: object().shape({
    title: string().required('*Required'),
  }),
});

interface OwnProps {
  closePanel: () => void;
}

type Props = OwnProps & ChatroomFieldProps;

const LiveLikeChatForm: FC<Props> = ({ closePanel, handleBackToWidgets }) => {
  const dispatch = useDispatch();

  const { id: itemId } = useSelector(getItemDetails);

  const { isReady, savedChatroom } = useChatrooms();

  useWillMount(() => {
    dispatch(loadResource('chatrooms', { page: 1 }));
  });

  const savedValues: Optional<FormValues> = useMemo(() => savedChatroom && { chatroom: savedChatroom }, [
    savedChatroom,
  ]);

  const initialValues: FormValues = {
    ...defaultValues,
    ...savedValues,
  };

  const handleCancel = (resetForm: () => void) => () => {
    resetForm();
    handleBackToWidgets();
  };

  const handleSave = async (data: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    const { chatroom, patch } = data;

    const createRoomData = { ...chatroom, itemId: Number(itemId) };

    dispatch(createChatRoom(createRoomData, patch));

    resetForm();
    handleBackToWidgets();
  };

  if (!isReady) {
    return <Loader position="absolute" />;
  }

  return (
    <Formik<FormValues> initialValues={initialValues} onSubmit={handleSave} validationSchema={validationSchema}>
      {({ resetForm }) => (
        <StretchedForm>
          <FormAccordionHelper initialValues={initialValues} closePanel={closePanel} index={SectionType.LiveLike} />
          <StretchedFlex>
            <ChatroomField handleBackToWidgets={handleBackToWidgets} />
            <AccordionFooter accordionLabel="audience_engagement_widgets" handleCancel={handleCancel(resetForm)} />
          </StretchedFlex>
        </StretchedForm>
      )}
    </Formik>
  );
};

export default LiveLikeChatForm;
